import { useBiApiClientProvider } from "../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../contexts/ApiClientProviderContext.ts";
import { getPivotExportWarning } from "../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../common/export/ExcelReportExport.tsx";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import type { SortField } from "../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext.ts";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { selectPreviewMode, selectSessionId } from "../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../store/selectors/devToolsSelectors.ts";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import ViewContainer from "../../viewer/common/ViewContainer.tsx";
import ViewerParameters from "../../viewer/common/ViewerParameters.tsx";
import useGridStateViewer from "../../viewer/pivot/hooks/useGridStateViewer.ts";
import { useRequestConfigViewer } from "../../viewer/pivot/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../viewer/pivot/PreviewComponent.tsx";
import type { PivotRunState } from "../../viewer/storage/runState.ts";
import ChangePreviewModeButton from "../common/ChangePreviewModeButton.tsx";
import useCommonReportHandlers from "../common/hooks/useCommonReportHandlers.ts";

interface Props {
  runState: PivotRunState;
}
export default function PivotViewer({ runState }: Props) {
  const { requestRunPivotExport } = useBiApiClientProvider<BiApiClient>();
  const previewMode = useSelector(selectPreviewMode);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const handlers = useCommonReportHandlers(runState.values);
  const { onRefreshGrid, onSortingUpdate } = useBiCommonDataContext();
  const { runPivotReport } = useEnhancedBiApiClientProvider();
  const sessionId = useSelector(selectSessionId);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    values: runState.values,
    sorts: runState.sort,
    isQueryLoggingActive,
    sessionId,
  });

  const gridState = useGridStateViewer({
    rows: runState.rows,
    columns: runState.columns,
    values: runState.values,
    sorts: runState.sort,
    requestConfig: viewConfig,
    updateDevTools: handlers.updateDevTools,
    setEtag: handlers.setEtag,
    runReport:
      runPivotReport ??
      (() => {
        throw new Error("runPivotReport is not supported");
      }),
  });

  const handleSortingUpdate = useCallback(
    (sortFields: SortField[]) => {
      onSortingUpdate?.(sortFields);
    },
    [onSortingUpdate]
  );

  return (
    <>
      <ExcelReportExport
        mode="run"
        requestConfig={viewConfig}
        dimensions={runState.dimensions}
        requestExport={requestRunPivotExport}
        warning={getPivotExportWarning(gridState, runState.rows.length)}
      />
      <ViewContainer
        Parameters={
          runState.showParameters ? (
            <ViewerParameters
              fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
              initialDetailedView={runState.settings.detailedView}
            />
          ) : (
            <></>
          )
        }
        Preview={
          <PreviewComponent
            gridState={gridState}
            previewMode={previewMode}
            settings={runState.settings}
            onDrillDown={handlers.onDrillDown}
            values={runState.values}
            rows={runState.rows}
            columns={runState.columns}
            onSortingUpdate={handleSortingUpdate}
            sorts={runState.sort}
          />
        }
        ActionPanel={<PreviewActions onRefresh={onRefreshGrid} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
      />
    </>
  );
}
